import { useCallback, useEffect, useState } from "react";
import { Input, message, Select, Tooltip } from "antd";
import { fontWeightOptions } from "shared/constants/canvas";
import styles from "./FontProperty.module.scss";
import { SketchPicker } from "react-color";
import { TCanvasAction } from "screens/designStudio/editor/canvasContainer/Canvas";
import useFontProperty from "./fontProperty.hooks/useFontProperty";
import useFonts from "./fontProperty.hooks/useFonts";
import { useBrandsFonts } from "./fontProperty.hooks/useBrandsFonts";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { useCAMFonts, useCAMHookWithFallback } from "shared/components/media";
import { loadFont as loadFontFn } from "utils/helpers";

type Props = {
  textbox?: fabric.Textbox;
  presetColors: string[];
  canvasAction?: TCanvasAction;

  onChange: (key: keyof fabric.Textbox) => (value: string | number) => void;
};

const FontProperty = (props: Props) => {
  const { onChange, presetColors } = props;
  const {
    fontWeight = "normal",
    charSpacing,
    lineHeight,
    fill,
    name,
  } = props.textbox || {};

  const { shouldUseBrandFonts } = useBrandsFonts();

  const { fontFamilies, loadFont } = useCAMHookWithFallback(
    useCAMFonts,
    useFonts,
    useBrandsFonts,
    shouldUseBrandFonts,
  )();

  const onFontChange = useCallback(
    async (value: string) => {
      // If value (which is selected font family) is in the font mappings,
      //   first load that font dynamically.
      try {
        await loadFont(value, loadFontFn);
        onChange("fontFamily")(value);
      } catch (err) {
        message.warning(`${value} could not be loaded`);
      }
    },
    [onChange, loadFont],
  );

  const [selectedColor, setSelectedColor] = useState<string>(
    (fill as string) || "#ffffff",
  );
  useEffect(() => {
    if (!name || !fill) return;

    setSelectedColor(fill as string);
  }, [fill, name]);

  const [displayColorPicker, setDisplayColorPicker] = useState<boolean>(false);

  const { textbox, canvasAction } = props;
  const { fontName, fontSize, setFontSize } = useFontProperty({
    textbox,
    canvasAction,
  });

  const onFontSizeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();

      const {
        target: { value },
      } = e;
      setFontSize(value);

      const isNumber = !Number.isNaN(+value);
      if (!isNumber) return;

      onChange("fontSize")(value);
    },
    [onChange, setFontSize],
  );

  const [showFontSizeArrows, toggleFontSizeArrows] = useState<boolean>(false);
  const onMouseEnterFontSizeInput = useCallback(() => {
    toggleFontSizeArrows(true);
  }, []);
  const onMouseLeaveFontSizeInput = useCallback(() => {
    toggleFontSizeArrows(false);
  }, []);

  const onArrowClick = useCallback(
    (which: "up" | "down") => () => {
      if (!textbox) return;

      const alteringValue = which === "up" ? 1 : -1;

      let updatingFontSize: number = 0;
      if (fontSize === "Mixed") {
        // If mixed, take the largest font size among sub-strings and increase one from that.
        // Related slack conversation: https://constellationagency.slack.com/archives/C02T6FSM72M/p1646164618007609
        const { styles } = textbox;
        const largestSize = Object.values(styles).reduce<number>(
          (acc, lineStyles) => {
            const size = Object.values(
              (lineStyles as Record<string, any>) || {},
            ).reduce<number>((innerAcc, charStyles) => {
              const { fontSize } = charStyles || {};
              if (!fontSize) return innerAcc;

              let size: number = 0;
              if (typeof fontSize === "string") {
                size = Number.parseInt(fontSize) || 0;
              }

              return innerAcc < size ? size : innerAcc;
            }, 0);

            return acc < size ? size : acc;
          },
          0,
        );

        const { fontSize: rootFontSize } = textbox;

        const parsedFontSize: number = Number.parseInt(`${rootFontSize}`) || 0;

        updatingFontSize =
          largestSize < parsedFontSize
            ? parsedFontSize + alteringValue
            : largestSize + alteringValue;
      } else {
        const parsedFontSize =
          typeof fontSize === "string" ? Number.parseInt(fontSize) : fontSize;
        updatingFontSize = parsedFontSize + alteringValue;
      }

      onChange("fontSize")(updatingFontSize);
    },
    [fontSize, textbox, onChange],
  );

  return (
    <div className={styles.FontProperty}>
      <div className={styles.FontSelect}>
        <Select showSearch={true} value={fontName} onChange={onFontChange}>
          {fontFamilies.map((option, idx) => (
            <Select.Option key={`${option}-${idx}`} value={option}>
              <Tooltip
                title={option}
                trigger="hover"
                placement="right"
                mouseEnterDelay={0.5}
              >
                {option}
              </Tooltip>
            </Select.Option>
          ))}
        </Select>
      </div>
      <div className={styles.FontWeightStyleSize}>
        <span>
          <Select<string | number>
            value={fontWeight}
            onChange={onChange("fontWeight")}
          >
            {fontWeightOptions.map(option => (
              <Select.Option
                key={`${option}-font-weight-option`}
                value={option}
              >
                {option}
              </Select.Option>
            ))}
          </Select>
        </span>
        <span className={styles.FontSizeProperty}>
          <div
            className={styles.FontSizeInput}
            onMouseEnter={onMouseEnterFontSizeInput}
            onMouseLeave={onMouseLeaveFontSizeInput}
          >
            <Input onChange={onFontSizeChange} value={fontSize} />
            {showFontSizeArrows && (
              <div className={styles.Arrows}>
                <div className={styles.Wrapper}>
                  <CaretUpOutlined
                    className={styles.Up}
                    onClick={onArrowClick("up")}
                  />
                  <CaretDownOutlined
                    className={styles.Down}
                    onClick={onArrowClick("down")}
                  />
                </div>
              </div>
            )}
          </div>
        </span>
      </div>
      <div className={styles.FontColor}>
        <div className={styles.FontColorInputContainer}>
          <Input value={selectedColor} type="text" />
          <span
            style={{
              background: selectedColor,
            }}
            onClick={() => {
              setDisplayColorPicker(!displayColorPicker);
            }}
          />
        </div>

        <div
          className={`${styles.ColorPicker} ${
            !displayColorPicker ? styles.Hide : ""
          }`}
        >
          <SketchPicker
            disableAlpha={true}
            color={selectedColor}
            onChange={colorResult => {
              setSelectedColor(colorResult.hex);
            }}
            onChangeComplete={colorResult => onChange("fill")(colorResult.hex)}
            presetColors={presetColors}
          />
        </div>
      </div>

      <div className={styles.FontLineProperties}>
        <span key={`font-line-property-Character`}>
          <Input
            type="number"
            step={0.1}
            value={charSpacing}
            onChange={e => onChange("charSpacing")(e.target.value)}
          />
          <div className={styles.Label}>Character</div>
        </span>
        <span key={`font-line-property-Line`}>
          <Input
            type="number"
            step={0.01}
            value={lineHeight}
            onChange={e => onChange("lineHeight")(e.target.value)}
          />
          <div className={styles.Label}>Line</div>
        </span>
        <span key={`font-line-property-Paragraph`}>
          <Input value={0} disabled={true} />
          <div className={styles.Label}>Paragraph</div>
        </span>
      </div>
    </div>
  );
};

export default FontProperty;
